<template>
  <div class="space-y-8 max-w-4xl mx-auto px-4 sm:px-6">
    <!-- Hero Section -->
    <div class="space-y-6">
      <!-- SEO-optimized eyebrow -->
      <p
        class="text-xs uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold"
      >
        JUMMA TUL WIDA 2026 - PAKISTAN
      </p>

      <!-- Hero section -->
      <div class="space-y-4">
        <!-- H1 - SEO optimized -->
        <h1
          class="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 dark:text-gray-100 leading-tight"
        >
          {{ t("pages.jummatulWida2026.header.romanUrduTitle") }}
        </h1>

        <!-- Subtitle with context -->
        <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400">
          {{ t("pages.jummatulWida2026.dates.description") }}
        </p>
      </div>

      <!-- Main Date Card - Blue emphasis for Jumma -->
      <div
        class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 rounded-2xl p-6 border-2 border-blue-300 dark:border-blue-700 shadow-lg"
      >
        <div class="flex items-start gap-3 mb-4">
          <svg
            class="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <div class="flex-1">
            <p
              class="text-sm font-bold text-blue-700 dark:text-blue-400 mb-1 uppercase tracking-wide"
            >
              Ramzan 2026 Ka Aakhri Jumma
            </p>
            <p
              class="text-4xl sm:text-5xl font-black text-gray-900 dark:text-gray-100 mb-2"
            >
              20 March 2026
            </p>
            <p
              class="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300 mb-3"
            >
              Jumma (Friday)
            </p>
            <div class="flex flex-wrap gap-2 mb-3">
              <span
                class="text-sm font-semibold text-blue-700 dark:text-blue-300 bg-white/60 dark:bg-gray-800/60 rounded-md px-3 py-1"
              >
                Alvida Jumma
              </span>
              <span
                class="text-sm font-semibold text-blue-700 dark:text-blue-300 bg-white/60 dark:bg-gray-800/60 rounded-md px-3 py-1"
              >
                Jummatul Wida
              </span>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ t("pages.jummatulWida2026.dates.note") }}
            </p>
          </div>
        </div>
        <div
          class="flex items-center gap-2 text-sm sm:text-base text-blue-800 dark:text-blue-300 bg-white/60 dark:bg-gray-800/60 rounded-lg px-3 py-2 w-fit"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span class="font-semibold"
            >Jumma ki namaz masjid mein zaroor parhein</span
          >
        </div>
      </div>

      <!-- Shab-e-Qadr Coincidence Notice (if applicable) -->
      <div
        class="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-yellow-950/40 rounded-xl p-5 border border-amber-200 dark:border-amber-800"
      >
        <div class="flex items-start gap-3">
          <svg
            class="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
            />
          </svg>
          <div class="text-sm text-gray-700 dark:text-gray-300">
            <p class="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Khas Fazilat:
            </p>
            <p>{{ t("pages.jummatulWida2026.dates.coincidence") }}</p>
          </div>
        </div>
      </div>

      <!-- Youm-ul-Quds Connection -->
      <div
        class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/40 dark:to-emerald-950/40 rounded-xl p-5 border border-green-200 dark:border-green-800"
      >
        <div class="flex items-start gap-3">
          <svg
            class="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div class="text-sm text-gray-700 dark:text-gray-300">
            <p
              class="font-bold text-gray-900 dark:text-gray-100 mb-2 text-base"
            >
              {{ t("pages.jummatulWida2026.qudsDay.title") }}
            </p>
            <p class="mb-2">{{ t("pages.jummatulWida2026.qudsDay.description") }}</p>
            <p class="text-xs italic text-gray-600 dark:text-gray-400">
              {{ t("pages.jummatulWida2026.qudsDay.message") }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Ahmiyat Section -->
    <div class="space-y-4">
      <h2
        class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
      >
        {{ t("pages.jummatulWida2026.significance.title") }}
      </h2>
      <div class="prose dark:prose-invert max-w-none">
        <p class="text-base leading-relaxed text-gray-700 dark:text-gray-300">
          {{ t("pages.jummatulWida2026.significance.description") }}
        </p>
      </div>
      <div class="grid sm:grid-cols-2 gap-4">
        <div
          v-for="(virtue, index) in virtues"
          :key="index"
          class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-xl p-5 border border-blue-200 dark:border-blue-800"
        >
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ virtue }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Ibadat Ka Tarika -->
    <div class="space-y-4">
      <h2
        class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
      >
        {{ t("pages.jummatulWida2026.worship.title") }}
      </h2>

      <div class="space-y-3">
        <div
          v-for="(activity, index) in worshipActivities"
          :key="index"
          class="flex items-start gap-3 bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
        >
          <div
            class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center"
          >
            <span class="text-xs font-bold text-blue-700 dark:text-blue-300">{{
              index + 1
            }}</span>
          </div>
          <p class="text-sm text-gray-700 dark:text-gray-300">{{ activity }}</p>
        </div>
      </div>

      <!-- Special Duas Section -->
      <div
        class="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/40 rounded-2xl p-6 border-2 border-purple-200 dark:border-purple-800 mt-6"
      >
        <div class="flex items-center gap-2 mb-4">
          <svg
            class="w-6 h-6 text-purple-600 dark:text-purple-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            Jumma tul Wida Ki Khas Duain
          </h3>
        </div>

        <div class="space-y-5">
          <div
          v-for="(dua, index) in duaList"
          :key="index"
          class="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4"
        >
            <p
              class="text-xs font-bold text-purple-700 dark:text-purple-300 mb-2 uppercase tracking-wide"
            >
              {{ dua.name }}
            </p>
            <p
              class="text-xl sm:text-2xl font-arabic text-gray-900 dark:text-gray-100 leading-loose mb-3 text-right"
              dir="rtl"
            >
              {{ dua.arabic }}
            </p>
            <p
              class="text-sm font-semibold text-purple-700 dark:text-purple-300 italic"
            >
              {{ dua.roman }}
            </p>
          </div>
        </div>

        <!-- Additional Dua Topics -->
        <div
          class="mt-5 pt-5 border-t border-purple-200 dark:border-purple-800"
        >
          <p
            class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3"
          >
            Is din yeh duain bhi karein:
          </p>
          <div class="grid sm:grid-cols-2 gap-2">
            <div
              v-for="(topic, index) in worshipDuas"
              :key="index"
              class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 bg-white/40 dark:bg-gray-700/40 rounded-md px-3 py-2"
            >
              <svg
                class="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ topic }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Practical Tips -->
    <div class="space-y-4">
      <h2
        class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
      >
        {{ t("pages.jummatulWida2026.practical.title") }}
      </h2>

      <div class="grid sm:grid-cols-2 gap-4">
        <div
          v-for="(tip, index) in practicalTips"
          :key="index"
          class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ tip }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- SEO Content - Collapsible -->
    <details class="group mt-8">
      <summary class="cursor-pointer list-none">
        <div
          class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <span class="font-semibold text-gray-900 dark:text-gray-100">
            Jumma tul Wida 2026 - Complete Guide & FAQs
          </span>
          <svg
            class="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </summary>

      <div class="mt-4 space-y-6 text-gray-700 dark:text-gray-300">
        <!-- SEO-optimized intro paragraph -->
        <div class="prose dark:prose-invert max-w-none">
          <p class="text-base leading-relaxed">
            Jumma tul Wida 2026 Pakistan mein <strong>20 March 2026</strong> ko
            hai (umeedi tarikh, chand dekhi par munhasir). Jumma tul Wida, jise
            <strong>Alvida Jumma</strong> ya <strong>Jummatul Wida</strong> bhi
            kaha jata hai, Ramadan 2026 ka aakhri Jumma hai. Ye
            <strong>Jumma ka din</strong> haftay ka sabse afzal din hai, aur jab
            yeh Ramzan ke aakhri dinon mein aata hai to is ki ahmiyat aur barh
            jati hai. Pakistan mein is din masjidon mein bari tadat mein
            musalman jama hote hain aur apni Ramzan ki ibadat ki qabooliyat ke
            liye dua karte hain. Jumma tul Wida ke din
            <strong>Youm-ul-Quds</strong> bhi mana jata hai, jis mein Pakistan
            bhar mein log Qibla-e-Awwal (Baitul Muqaddas) ki aazadi ke liye apna
            ehtejaj aur aikta ka izhaar karte hain. Is din
            <a
              href="/calendar/"
              class="text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >Ramadan calendar</a
            >
            se timings confirm karen aur
            <a
              href="/dua/"
              class="text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >khas duain</a
            >
            parhein.
          </p>
        </div>

        <!-- FAQ Section for rich results -->
        <div class="space-y-4">
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            Frequently Asked Questions
          </h2>

          <div class="space-y-3">
            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Jumma tul Wida 2026 kab hai Pakistan mein?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Jumma tul Wida 2026 Pakistan mein
                <strong>20 March 2026</strong> ko hai (umeedi). Yeh Ramadan 2026
                ka aakhri Jumma hai. Exact date chand dekhi par depend karti
                hai. Agar Ramzan 29 rozay ka hua to Jumma tul Wida
                <strong>13 March 2026</strong> ko hoga.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Jumma tul Wida ki kya fazilat hai?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Jumma tul Wida do fazilaton ko jama karta hai: Jumma ki fazilat
                aur Ramzan ke aakhri dinon ki barkat. Jumma ka din haftay ka
                afzal din hai, aur jab yeh Ramzan ke aakhri dinon mein aata hai
                to is ki ahmiyat aur barh jati hai. Is din qabooliyat ki duaon
                ki umeed hoti hai.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Jumma tul Wida mein kya karna chahiye?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Jumma tul Wida mein yeh karein: (1) Ghusl karen aur saf kapray
                pehnein, (2) Jumma ki namaz masjid mein zaroor parhein, (3)
                Surah Al-Kahf ki tilawat karen, (4) Darood sharif zyada parhein,
                (5) Apne walidain, rishtedaaron, aur tamam musalmano ke liye dua
                karen, (6) Pakistan ki aman o salamati ki dua karen, (7) Apni
                Ramzan ki ibadat ki qabooliyat mangte huye Allah se magfirat
                talash karen.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Jumma tul Wida aur Youm-ul-Quds mein kya connection hai?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Jumma tul Wida ke din Pakistan bhar mein Youm-ul-Quds (Al-Quds
                Day) bhi mana jata hai. Is din log Qibla-e-Awwal yaani Masjid
                Al-Aqsa aur Baitul Muqaddas ki aazadi ke liye apna ehtejaj aur
                aikta ka izhaar karte hain. Hum apne Falasteeni bhaiyon ke saath
                solidarity ka izhaar karte hain aur un ki madad ki dua karte
                hain.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Kya Jumma tul Wida aur Shab-e-Qadr ek din ho sakte hain?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Haan, kabhi kabhi Jumma tul Wida 27 Ramadan (jo Shab-e-Qadr ki
                most likely night hai) ke saath parhta hai. Jab aisa hota hai to
                is din ki fazilat aur zyada barh jati hai kyunke Jumma ki barkat
                aur Shab-e-Qadr ki fazilat dono jama ho jati hain. 2026 mein
                agar 27 Ramadan Friday ko parhey to yeh aik historic mubarak din
                hoga.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Jumma tul Wida ke baad kya karna chahiye?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Jumma tul Wida ke baad Ramzan ke aakhri dinon ki ibadat ka plan
                banayein. Agar itikaf nahi kar sakte to ghar mein hi zyada
                ibadat karen. Taaq raaton (especially 27th aur 29th night) mein
                Shab-e-Qadr ki talash karen. Zakat aur Fitrana ka hisaab
                complete karen. Eid ki tayyari shuru karen lekin ibadat ko
                priority dein.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Jumma tul Wida ki kaunsi duain parhni chahiye?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Jumma tul Wida par yeh duain karein: (1) "Allahummaghfir lil
                muslimina wal muslimati" (tamam musalmanon ki magfirat ki dua),
                (2) "Allahumma ihfaz Pakistan" (Pakistan ki hifazat ki dua), (3)
                Apne walidain ki magfirat aur lambi umar ki dua, (4) Tamam
                musalman ummah ki aikta ki dua, (5) Falasteen aur Kashmir ke
                mazloom musalmano ki madad ki dua, (6) Apne gunahon ki maafi ki
                dua.
              </p>
            </details>
          </div>
        </div>

        <!-- Related Pages - Internal linking -->
        <div
          class="bg-blue-50 dark:bg-blue-950 rounded-lg p-4 border border-blue-200 dark:border-blue-800"
        >
          <p
            class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3"
          >
            Related Pages:
          </p>
          <div class="grid sm:grid-cols-2 gap-2">
            <a
              href="/shab-e-qadr/"
              class="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Shab-e-Qadr 2026
            </a>
            <a
              href="/itikaf/"
              class="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Itikaf 2026 Dates
            </a>
            <a
              href="/calendar/"
              class="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Ramadan Calendar 2026
            </a>
            <a
              href="/dua/"
              class="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Ramzan Ki Duain
            </a>
          </div>
        </div>

        <!-- Final spiritual message -->
        <div
          class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-xl p-5 border border-blue-200 dark:border-blue-800"
        >
          <p
            class="text-sm leading-relaxed text-gray-700 dark:text-gray-300 text-center"
          >
            <strong class="text-blue-700 dark:text-blue-400 block mb-2">{{
              t("pages.jummatulWida2026.content.spiritualMessage")
            }}</strong>
            <span class="text-xs block text-gray-600 dark:text-gray-400">{{
              t("pages.jummatulWida2026.content.pakistanMessage")
            }}</span>
          </p>
        </div>
      </div>
    </details>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t, tm } = useI18n();

const asArray = (value: unknown) => (Array.isArray(value) ? value : []);

const virtues = computed(
  () => asArray(tm("pages.jummatulWida2026.significance.virtues")) as string[],
);
const worshipActivities = computed(
  () => asArray(tm("pages.jummatulWida2026.worship.activities")) as string[],
);
const duaList = computed(
  () => asArray(tm("pages.jummatulWida2026.duas.list")) as Array<{
    name: string;
    arabic: string;
    roman: string;
  }>,
);
const worshipDuas = computed(
  () => asArray(tm("pages.jummatulWida2026.worship.duas")) as string[],
);
const practicalTips = computed(
  () => asArray(tm("pages.jummatulWida2026.practical.tips")) as string[],
);

// Set meta tags
useHead({
  title: t("pages.jummatulWida2026.meta.title"),
  meta: [
    { name: "description", content: t("pages.jummatulWida2026.meta.description") },
    { name: "keywords", content: t("pages.jummatulWida2026.meta.keywords") },
    // Open Graph
    { property: "og:title", content: t("pages.jummatulWida2026.meta.title") },
    {
      property: "og:description",
      content: t("pages.jummatulWida2026.meta.description"),
    },
    { property: "og:type", content: "article" },
    // Twitter Card
    { name: "twitter:title", content: t("pages.jummatulWida2026.meta.title") },
    {
      name: "twitter:description",
      content: t("pages.jummatulWida2026.meta.description"),
    },
  ],
});

// Add FAQ Schema
useSchemaOrg([
  {
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Jumma tul Wida 2026 kab hai Pakistan mein?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Jumma tul Wida 2026 Pakistan mein 20 March 2026 ko hai (umeedi). Yeh Ramadan 2026 ka aakhri Jumma hai. Agar Ramzan 29 rozay ka hua to 13 March 2026 ko ho sakta hai. Exact date chand dekhi par depend karti hai.",
        },
      },
      {
        "@type": "Question",
        name: "Jumma tul Wida ki kya fazilat hai?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Jumma tul Wida do fazilaton ko jama karta hai: Jumma ki fazilat aur Ramzan ke aakhri dinon ki barkat. Is din qabooliyat ki duaon ki umeed hoti hai.",
        },
      },
      {
        "@type": "Question",
        name: "Jumma tul Wida mein kya karna chahiye?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ghusl karen, saf kapray pehnein, Jumma ki namaz masjid mein parhein, Surah Al-Kahf ki tilawat karen, darood sharif parhein, aur apne walidain, rishtedaaron aur Pakistan ki aman ki dua karen.",
        },
      },
      {
        "@type": "Question",
        name: "Jumma tul Wida aur Youm-ul-Quds mein kya connection hai?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Jumma tul Wida ke din Pakistan bhar mein Youm-ul-Quds bhi mana jata hai. Is din log Masjid Al-Aqsa aur Baitul Muqaddas ki aazadi ke liye apna ehtejaj aur aikta ka izhaar karte hain.",
        },
      },
      {
        "@type": "Question",
        name: "Kya Jumma tul Wida aur Shab-e-Qadr ek din ho sakte hain?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Haan, kabhi kabhi Jumma tul Wida 27 Ramadan (Shab-e-Qadr ki most likely night) ke saath parhta hai. Jab aisa hota hai to is din ki fazilat aur zyada barh jati hai.",
        },
      },
      {
        "@type": "Question",
        name: "Jumma tul Wida ki kaunsi duain parhni chahiye?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tamam musalmanon ki magfirat ki dua, Pakistan ki hifazat ki dua, walidain ki magfirat ki dua, musalman ummah ki aikta ki dua, aur mazloom musalmano ki madad ki dua parhein.",
        },
      },
    ],
  },
]);
</script>

